'use client';
import { useSoundStore } from './sound.store';

/**
 * Two in-app sound cues, synthesized rather than shipped as files.
 *
 * **Why synthesized:** an audio file is an asset to host, cache-bust and get 404s from, and both
 * cues are under half a second of pure tone. The Web Audio API renders them from nothing, so there
 * is no network request, no CSP question and nothing to keep in `public/`.
 *
 * **Two cues, not three** (owner, 2026-09-05). Everything that goes to plan shares ONE sound —
 * entry, exit, stop trailed, target hit — because a trader learns a single "something happened"
 * note far faster than a vocabulary of them. Anything that FAILED or needs a look gets a second,
 * firmer one, because that difference is the only one worth hearing across a room. Which of the two
 * plays is decided by `level` on the activity-log event, a judgement the server already makes once
 * (`notification.format.ts` leads an alarm with 🚨 and a warning with ⚠️) — see `SoundAlerts`.
 *
 * **Mild is a requirement, not a preference** (owner: "it should not disturb the user"). Both cues
 * are short and shaped with a real attack and decay — an abrupt start is what makes a soft sound
 * feel harsh. A lowpass takes the edge off the upper harmonics so it sits in the room rather than
 * on top of it. That shaping, not the absolute level, is what keeps them from being startling.
 *
 * **Levels raised +10 dB on 2026-09-10** (owner, after checking a live client device: "the volume is
 * very less ... users can actually hear"). Peak gain went 0.045 → 0.142 and 0.07 → 0.221 against a
 * 1.0 ceiling. +10 dB is the textbook "twice as loud" to the ear — doubling the NUMBER would only be
 * +6 dB, which is a difference you notice rather than one that carries. Both cues are still well
 * under a third of full scale, and the envelope and lowpass are untouched, so they are louder
 * without being sharper. `sound.spec.ts` pins the ratio between the two cues.
 */

let audioCtx: AudioContext | null = null;
let lastPlayAt = 0;

/** Two cues inside this window collapse into one. The per-frame guard in `SoundAlerts` is the real
 *  protection; this is the backstop for any other caller. */
const MIN_GAP_MS = 220;

/**
 * Peak gain of each cue, against a 1.0 ceiling. Raised +10 dB on 2026-09-10 — see the note at the
 * top of this file. The alert stays a touch above the trade cue, the same ratio it always had, so
 * the "something needs a look" cue still carries further across a room than the routine one.
 */
export const TRADE_PEAK_GAIN = 0.142;
export const ALERT_PEAK_GAIN = 0.221;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    if (!audioCtx) {
      const Ctor = window.AudioContext
        || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      audioCtx = new Ctor();
    }
    if (audioCtx.state === 'suspended') void audioCtx.resume();
    return audioCtx;
  } catch {
    return null;
  }
}

/**
 * Browsers refuse to play audio until the user has interacted with the page, so the context is
 * created and resumed on the first gesture.
 *
 * The listener removes itself only once the context is genuinely `running` — not after one call.
 * A gesture can arrive while the tab is still backgrounded, or `resume()` can reject, and a
 * `{ once: true }` listener would spend its single attempt on that and leave the client silent for
 * the rest of the session with nothing to show for it.
 */
if (typeof window !== 'undefined') {
  const events = ['pointerdown', 'keydown', 'touchstart'] as const;
  const arm = () => {
    const ctx = getCtx();
    if (ctx && ctx.state === 'running') {
      events.forEach(name => window.removeEventListener(name, arm));
    }
  };
  events.forEach(name => window.addEventListener(name, arm, { passive: true }));
}

type Note = {
  /** Hz. */
  freq: number;
  /** Seconds from the start of the cue. */
  at: number;
  /** Seconds. */
  duration: number;
  type?: OscillatorType;
};

function playCue(notes: Note[], peakGain: number, cutoffHz: number, force = false): void {
  if (!force && !useSoundStore.getState().enabled) return;

  const now = Date.now();
  if (!force && now - lastPlayAt < MIN_GAP_MS) return;

  const ctx = getCtx();
  // Not yet unlocked by a gesture: stay silent rather than throwing. The next event will play once
  // the client has clicked anything at all.
  if (!ctx || ctx.state !== 'running') return;
  lastPlayAt = now;

  // One filter and one master gain for the whole cue, so the notes blend instead of clicking
  // against each other.
  const master = ctx.createGain();
  master.gain.value = 1;
  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = cutoffHz;
  lowpass.Q.value = 0.7;
  master.connect(lowpass).connect(ctx.destination);

  const start = ctx.currentTime + 0.01;
  for (const note of notes) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = note.type || 'sine';
    osc.frequency.value = note.freq;

    const t0 = start + note.at;
    const t1 = t0 + note.duration;
    // A real envelope: ~18ms attack, then an exponential decay to silence. Ramping from an audible
    // value (rather than from 0) is what an exponential ramp requires, and the tiny floor is
    // inaudible.
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peakGain, t0 + 0.018);
    gain.gain.exponentialRampToValueAtTime(0.0001, t1);

    osc.connect(gain).connect(master);
    osc.start(t0);
    osc.stop(t1 + 0.03);
  }

  // Release the nodes once the cue has finished rather than leaving them attached for the life of
  // the page — this fires on every trade event of a trading day.
  const end = start + Math.max(...notes.map(note => note.at + note.duration)) + 0.1;
  window.setTimeout(() => {
    try { master.disconnect(); lowpass.disconnect(); } catch { /* already torn down */ }
  }, Math.max(0, (end - ctx.currentTime) * 1000) + 50);
}

/**
 * Everything that went to plan: entry filled, position closed, stop trailed, target hit.
 *
 * A soft rising perfect fourth (E6 → A6), the two notes overlapping slightly so it reads as one
 * gesture rather than two beeps.
 */
export function playTradeSound(force = false): void {
  playCue(
    [
      { freq: 1318.5, at: 0, duration: 0.09 },
      { freq: 1760, at: 0.07, duration: 0.13 },
    ],
    TRADE_PEAK_GAIN,
    3200,
    force,
  );
}

/**
 * Something failed or needs a look: an order the broker refused, a protective order that died, a
 * square-off, an entry still unfilled.
 *
 * Deliberately different in SHAPE, not just pitch — a falling pair with a repeat, on a triangle
 * wave, lower and a touch louder. Two cues that differ only in pitch are indistinguishable once
 * there is any noise in the room, which defeats the point of having two.
 */
export function playAlertSound(force = false): void {
  playCue(
    [
      { freq: 880, at: 0, duration: 0.1, type: 'triangle' },
      { freq: 587.3, at: 0.1, duration: 0.14, type: 'triangle' },
      { freq: 587.3, at: 0.28, duration: 0.16, type: 'triangle' },
    ],
    ALERT_PEAK_GAIN,
    2400,
    force,
  );
}

/**
 * The Settings "Test" buttons.
 *
 * `force` bypasses the enabled check and the throttle on purpose: the client is pressing the button
 * to decide whether to turn the setting ON, so it has to be audible while it is still OFF, and
 * pressing it twice in a row must play twice.
 */
export function playSoundPreview(kind: 'trade' | 'alert'): void {
  if (kind === 'alert') playAlertSound(true);
  else playTradeSound(true);
}
